import type { RouterAction } from "@bash/shared";
import { useTaskStore } from "@/stores/taskStore";
import { forwardAction, isDockWindow, trackDockTask } from "@/lib/dock-window";
import { useLiveStore } from "@/components/composer/live/liveStore";

export interface RouterLike {
  push(route: string): void;
}

/** The belt's gate: only an https link is ever opened, or held on the crown as a row (R-L11). */
export const isHttpsUrl = (url: string): boolean => /^https:\/\//.test(url);

/**
 * Perform a RouterAction on the web (spec 2026-09-17 §5.4): ONE belt for the
 * typed lane (useComposerConverse) and the Line's spoken lane (LineActions).
 * Returns true when the action was handled by navigation, so the inline card
 * closes; false when the action is a receipt to render, or a link this belt
 * refused or the browser blocked, which leaves the card as the tap target.
 *
 * Inside the shell's Dock window (contract 2026-09-23) the panel's own router
 * never moves, or the Dock would leave /dock: a navigation or a link goes to
 * the main window (forwardAction), "show my tasks" opens the Dock's shelf,
 * and a task the Dock started joins the shell's task feed. Every other
 * window never reaches those arms (forwardAction and trackDockTask are inert
 * outside the panel).
 */
export function performRouterAction(
  action: RouterAction,
  router: RouterLike
): boolean {
  switch (action.kind) {
    case "task_created":
    case "task_followup":
      // Composer redesign (founder 07-16): a spawned task renders as a
      // RECEIPT in the inline card, never a navigation yank, identical on
      // every screen. The card is the door; clicking it opens the task.
      // A birth in the Dock joins the shell's task feed (inert elsewhere).
      if (action.kind === "task_created")
        trackDockTask({ taskId: action.taskId, title: action.title });
      return false;
    case "app_control":
      if (forwardAction(action)) return true;
      // The New task page means a new task, whichever door you come through.
      // Every other door to it clears the selection first (the Skills, Agents
      // and MCP pages all do createNewTask() before they push); this one did
      // not, so the task the person was last on stayed selected under a screen
      // that says there is nothing here — the pill's subject, and an id the
      // task layout wrote back into the address bar (founder 2026-09-21).
      if (action.view === "task.new") useTaskStore.getState().createNewTask();
      // `route` is server-computed from the destination registry (packages/shared/src/app/destinations.ts), never model-authored.
      router.push(action.route);
      return true;
    case "show_tasks":
      if (isDockWindow()) useLiveStore.getState().setTrayOpen(true);
      else router.push("/home"); // the web task list
      return true;
    case "open_url": {
      // 2a: the connect link, server-minted from the vendor's redirect (never model-authored);
      // https only, like the desktop's belt.
      //
      // NOT `window.open(url, "_blank", "noopener,noreferrer")`: `noopener` among the FEATURES
      // makes the call return null by spec whether or not the tab opened, so the handle could
      // never tell us anything and the card stayed routed on every success. We open without the
      // features and sever the opener by hand, which is the same guarantee one step later. The
      // trade is `noreferrer`, which has no hand equivalent: the vendor's own OAuth page sees a
      // Referer it would have seen from any other link to it.
      //
      // A blocked open (Safari, or Chrome past the gesture window) returns null for real, and
      // that leaves the routed card showing the link as a tap target instead of closing on nothing.
      // FALSE, not true, and the two halves of this belt agree (whole-branch review): `true` means
      // handled, which closes the card; a URL this belt refused was never opened, so closing on it
      // dismisses the one thing the person could still act on. The overlay's belt returns without
      // dismissing for the same reason.
      if (!isHttpsUrl(action.url)) return false;
      if (forwardAction(action)) return true;
      const opened = window.open(action.url, "_blank");
      if (!opened) return false;
      opened.opener = null;
      return true;
    }
    case "nami_saved":
      return false; // rendered as the confirm state; the card auto-dismisses
    case "end_call":
      // The Line's reflex (spec 2026-09-21): a NO-OP on this belt, and the only action that is.
      // It is served on a spoken composer turn alone, so it never arrives on the typed lane; the
      // server has already hung up, and the call ending is what the person sees. There is no
      // route to push and nothing to leave as a tap target, so this belt navigates nothing.
      return false;
  }
}
